import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Modal, Button } from "@/Components/Common/Tailwind";
import { numberWithCommas } from "../../common/utils/format_util";
import { parsePackageTagline } from "../../pages/Package/packageTagline.mjs";
import { getPackageImageSrc } from "../../pages/Package/packageImage.mjs";
import { packageService } from "../../api/services/package.service";

interface PackageDetailProduct {
  id?: number;
  name?: string;
  price?: number;
  membership_price?: number;
  count?: number;
  reserved?: number;
  images?: any[];
  tagline?: string;
  // 상세 API(/package/items/:id/)에서 내려오는 상세 필드
  descript?: string;
  using_info?: string;
  simple_desc?: string;
}

interface PackageDetailModalProps {
  product: PackageDetailProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
}

const PackageDetailModal = ({
  product,
  isOpen,
  onClose,
  onConfirm,
  confirmLabel = "이 상품으로 좌석 선택",
}: PackageDetailModalProps) => {
  // 목록 API 아이템에는 상세 HTML(descript/using_info)이 없어, 열릴 때 상세 정보를 별도로 불러온다.
  const [detail, setDetail] = useState<PackageDetailProduct | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  const productId = product?.id;

  useEffect(() => {
    if (!isOpen || !productId) return;
    // 이미 같은 상품의 상세를 불러왔다면 재요청하지 않는다.
    if (detail?.id === productId) return;

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(false);
    packageService
      .getPackageItemById(String(productId))
      .then((res) => {
        if (!cancelled) setDetail(res.data as PackageDetailProduct);
      })
      .catch((error) => {
        console.error("패키지 상세 조회 실패", error);
        if (!cancelled) setDetailError(true);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, productId, detail?.id]);

  if (!product) return null;

  // 가격/좌석 등 날짜 종속 정보는 목록 아이템(product)을, 상세 HTML은 상세 응답(detail)을 사용한다.
  const detailData = detail?.id === productId ? detail : null;
  // 이미지는 목록에서 이미 노출된 것을 우선 사용해 상세 로드 시 깜빡임을 막고, 없을 때만 상세 이미지를 쓴다.
  const images = Array.isArray(product.images) && product.images.length
    ? product.images
    : (detailData?.images ?? []);
  const imageSrcList = images.map((img) => getPackageImageSrc(img)).filter(Boolean);
  const taglineBadges = parsePackageTagline(product.tagline);
  const remainingCount = Math.max((product.count || 0) - (product.reserved || 0), 0);
  const hasMembershipPrice =
    product.membership_price && product.membership_price !== product.price;
  const simpleDesc = (detailData?.simple_desc || "").trim();
  const descriptHtml = (detailData?.descript || "").trim();
  const usingInfoHtml = (detailData?.using_info || "").trim();
  const hasDetailContent = Boolean(descriptHtml || usingInfoHtml);

  return (
    <Modal
      toggle={onClose}
      isOpen={isOpen}
      centered
      overlayClassName="backdrop-blur-none"
    >
      {/* 우상단 닫기(X) */}
      <button
        type="button"
        onClick={onClose}
        aria-label="닫기"
        className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center text-gray-500 transition-[color,scale] duration-150 hover:text-gray-800 active:scale-[0.96] before:absolute before:-inset-1 before:content-['']"
      >
        <X size={18} />
      </button>

      <div className="space-y-4">
        {/* 상품 요약: 썸네일(좌) + 상품명·가격·잔여좌석(우) 나란히 */}
        <div className="flex gap-3 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:75ms] [animation-fill-mode:both]">
          {imageSrcList.length > 0 ? (
            <img
              src={imageSrcList[0]}
              alt={product.name || "상품 이미지"}
              className="h-24 w-24 shrink-0 rounded-2xl object-cover ring-1 ring-inset ring-black/10"
            />
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col justify-center">
            <h5 className="line-clamp-2 text-lg font-bold leading-snug text-balance break-keep font-[Gmarket-B]">
              {product.name}
            </h5>
            {simpleDesc ? (
              <p className="mt-0.5 line-clamp-1 text-xs leading-5 text-gray-500 break-keep">{simpleDesc}</p>
            ) : null}
            <div className="mt-1.5 flex items-baseline gap-2">
              {hasMembershipPrice ? (
                <span className="text-xs text-gray-400 line-through tabular-nums">
                  {numberWithCommas(product.price)}원
                </span>
              ) : null}
              <span className="text-lg font-extrabold font-[Gmarket-B] tabular-nums">
                {numberWithCommas(hasMembershipPrice ? product.membership_price : product.price)}원
              </span>
            </div>
            <p className="mt-0.5 text-xs font-medium text-muted-foreground tabular-nums">
              잔여 {remainingCount}석 / 총 {product.count || 0}석
            </p>
          </div>
        </div>

        {/* 태그라인 */}
        {taglineBadges.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:150ms] [animation-fill-mode:both]">
            {taglineBadges.map((tagline, idx) => (
              <span
                key={`${product.id}-tagline-${idx}`}
                className="inline-flex max-w-full items-center rounded-full border border-[#f6475f]/20 bg-[#f6475f]/10 px-2 py-0.5 text-[10px] font-semibold leading-4 text-[#9f1239] break-all"
              >
                {tagline}
              </span>
            ))}
          </div>
        ) : null}

        {/* 상세 정보 */}
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:225ms] [animation-fill-mode:both]">
          {detailLoading && !detailData ? (
            <div className="space-y-2 rounded-xl bg-muted px-4 py-4">
              <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-11/12 animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-4/5 animate-pulse rounded bg-gray-200" />
            </div>
          ) : detailError ? (
            <p className="rounded-xl bg-muted px-4 py-4 text-center text-xs text-gray-500">
              상세 정보를 불러오지 못했습니다.
            </p>
          ) : hasDetailContent ? (
            <div className="max-h-[280px] space-y-4 overflow-y-auto rounded-xl bg-muted px-4 py-3">
              {descriptHtml ? (
                <div>
                  <p className="mb-1.5 text-xs font-bold text-muted-foreground">상품 상세</p>
                  <div
                    className="text-left text-xs leading-relaxed text-pretty break-keep text-gray-900 [&_[style*='color']]:!text-red-500 [&_img]:my-2 [&_img]:rounded-lg [&_img]:ring-1 [&_img]:ring-inset [&_img]:ring-black/10 [&_li]:mb-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_p:last-child]:mb-0 [&_p]:mb-1.5 [&_strong]:font-bold [&_strong]:text-red-500 [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: descriptHtml }}
                  />
                </div>
              ) : null}
              {usingInfoHtml ? (
                <div className={descriptHtml ? "border-t border-gray-200 pt-3" : ""}>
                  <p className="mb-1.5 text-xs font-bold text-muted-foreground">이용 안내</p>
                  <div
                    className="text-left text-xs leading-relaxed text-pretty break-keep text-gray-900 [&_[style*='color']]:!text-red-500 [&_img]:my-2 [&_img]:rounded-lg [&_img]:ring-1 [&_img]:ring-inset [&_img]:ring-black/10 [&_li]:mb-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_p:last-child]:mb-0 [&_p]:mb-1.5 [&_strong]:font-bold [&_strong]:text-red-500 [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: usingInfoHtml }}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <p className="rounded-xl bg-muted px-4 py-4 text-center text-xs text-gray-500">
              등록된 상세 정보가 없습니다.
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex justify-center gap-3">
        <Button
          variant="secondary"
          className="flex-1 min-h-[44px] transition active:scale-[0.96]"
          onClick={onClose}
        >
          닫기
        </Button>
        <Button
          variant="primary"
          className="flex-1 min-h-[44px] bg-[#f6475f] border-[#f6475f] hover:bg-[#e02e48] transition active:scale-[0.96]"
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default PackageDetailModal;
